import * as Immutable from 'immutable';
import RiderActions from 'common/redux/actions/rider';

import { entityMasterSingleEditFactory } from 'common/entity/generic/SingleEntityEditContainer';
import { MasterRider, RiderTypeType } from 'common/models/internal/MasterRider';
import { EmailAddress } from 'common/models/internal/BioData';
import { personnelTeamsSelector } from 'common/redux/selectors/personnelTeamsSelector';
import {
    ClientSelectors,
    getClientDimensionsMapping
} from 'common/redux/selectors/clientSelector';
import { rider as riderApi } from 'common/middleware/api';
import getSymbolFromCurrency from 'currency-symbol-map';
import { FieldTypeEnum } from 'common/enums/FieldTypeEnum';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import { RiderPersonaId } from 'common/models/internal/RiderPersonaId';
import CodeInputWithId from 'common/entity/generic/CodeInputWithId';
import DataUtils from 'common/utils/DataUtils';
import UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import HolidaySelector from 'common/entity/generic/HolidaySelector';
import { DateTimeFormat } from 'common/utils/TimeUtils';

import { IncludeTypeEnums } from 'common/enums/EntityMasterEnum';
import { SalesChannel } from 'common/models/internal/SalesChannel';

import { ENTITY_NAME_ID } from 'common/constants/entity/riderMaster';
import testIds from 'common/tests/entity/entityIds.json';
import CustomColumnHelpers from 'common/entity/helpers/CustomColumnHelpers';

const RiderTypeOptions = RiderTypeType.getOptions({
    translationKey: 'masterRiderProperties.types'
});

const getEntityIdInputFilters = ({ input }) => {
    let filters = new FilterListBuilder().add(
        new FilterBuilder()
            .withName('status')
            .withOperation(FilterOperation.Equals)
            .withValue('ACTIVE')
    );
    if (!DataUtils.isEmpty(input)) {
        filters = filters.add(
            new FilterBuilder()
                .withName('id')
                .withOperation(FilterOperation.ContainsToken)
                .withValue(input)
        );
    }
    return filters.build();
};

const arePjpFieldsDisabled = (entitySettings, entity) => {
    if (!entity?.riderPersona) {
        return !entitySettings.pjpSettings.enabled;
    }
    return (
        !entitySettings.pjpSettings.enabled || !entity.riderPersona.enablePjp
    );
};

const getDateFormat = localeSettings => {
    const isDateFormatMMDDYYYY = localeSettings.isDateFormatMMDDYYYY();
    const dateFormat = isDateFormatMMDDYYYY
        ? DateTimeFormat.SHEET_DATE_FORMAT_YYYY_MONTH_FIRST
        : DateTimeFormat.SHEET_DATE_FORMAT_YYYY;
    return dateFormat;
};

const RIDER_FIELDS = [
    {
        accessor: 'code',
        label: 'riderForm.code',
        helpText: 'riderForm.codeTooltip',
        placeholder: 'riderForm.codePlaceholder',
        halfWidth: false,
        disabledOnEdit: true,
        required: true,
        type: FieldTypeEnum.Text,
        component: CodeInputWithId,
        extraProps: ({ entity, field, props }) => {
            const {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                clientId
            } = props;
            return {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                id: entity.getIn(['id']),
                code: entity.getIn(field.absoluteAccessor),
                clientId
            };
        },
        onChangeHandler: ({ entity, value }) => {
            const { id, code } = value;
            if ('id' in value) {
                return entity.setId(id);
            } else if ('code' in value) {
                return entity.setCode(code);
            }
        },
        testId: testIds.rider.code
    },
    {
        accessor: 'name',
        label: 'riderForm.name',
        placeholder: 'riderForm.namePlaceholder',
        required: true,
        halfWidth: true,
        type: FieldTypeEnum.Text,
        testId: testIds.rider.name
    },
    {
        accessor: 'phoneNumber.phoneNumber',
        required: true,
        label: 'riderForm.phoneNumber',
        halfWidth: true,
        type: FieldTypeEnum.Phone,
        testId: testIds.rider.phone
    },
    {
        accessor: 'bioData.dateOfBirth',
        label: 'riderForm.dateOfBirth',
        type: FieldTypeEnum.DateTimePicker,
        halfWidth: true,
        placeholder: 'riderForm.dateOfBirthPlaceholder',
        extraProps: ({ props }) => {
            const dateFormat = getDateFormat(props.localeSettings);
            return { dateFormat };
        },
        testId: testIds.rider.dob
    },
    {
        accessor: 'bioData.emailAddress.emailAddress',
        label: 'riderForm.email',
        required: ({ props }) =>
            props.entitySettings.riderSettings.enforceEmailUniqueness,
        onChangeHandler: ({ entity, field, value }) => {
            const { emailAddress } = entity.bioData;
            if (emailAddress) {
                return entity.setIn(field.absoluteAccessor, value);
            } else {
                const newEmailAddress = EmailAddress.fromJS({
                    emailAddress: value
                });
                const newBio = entity.bioData.setEmailAddress(newEmailAddress);
                return entity.setBioData(newBio);
            }
        },
        halfWidth: true,
        placeholder: 'riderForm.emailPlaceholder',
        testId: testIds.rider.email
    },
    {
        accessor: 'riderPersonaId',
        required: true,
        type: FieldTypeEnum.RiderPersonaIdInputWithLimits,
        label: 'riderForm.riderPersonaId.label',
        placeholder: 'riderForm.riderPersonaId.placeholder',
        helpText: 'riderForm.riderPersonaId.helpText',
        isHidden: ({ isFieldHidden }) => isFieldHidden('riderPersona'),
        onChangeHandler: ({ entity, field, value: riderPersona }) => {
            let riderPersonaId = null;
            if (riderPersona) {
                riderPersonaId = RiderPersonaId.fromJS({
                    clientId: entity.clientId,
                    version: riderPersona.version,
                    riderPersonaId: riderPersona.id
                });
            }
            return entity
                .setRiderPersona(riderPersona)
                .setIn(field.absoluteAccessor, riderPersonaId);
        },
        extraProps: ({ t, entity, field, props }) => {
            const { unitConverter, entitySettings } = props;
            const customResources = CustomColumnHelpers.getCustomResources(
                entitySettings,
                'riderPersonaSettings'
            );
            const selectedRiderPersonaId = entity.getIn(field.absoluteAccessor)
                ?.riderPersonaId;
            const selectedRiderPersona = entity.riderPersona;
            const dataTestId = testIds.rider.riderPersona;

            return {
                t,
                riderPersonaId: selectedRiderPersonaId,
                getRiderPersonaIdFilters: input =>
                    getEntityIdInputFilters({ input }),
                unitConverter,
                riderPersona: selectedRiderPersona,
                'data-test-id': dataTestId,
                customResources
            };
        }
    },
    {
        accessor: 'shiftsInfo.shiftIds',
        type: FieldTypeEnum.ShiftIdInput,
        label: 'riderForm.shiftId.label',
        placeholder: 'riderForm.shiftId.placeholder',
        onChangeHandler: ({ entity, field, value }) =>
            entity.setIn(field.absoluteAccessor, value),
        extraProps: ({ entity, field }) => {
            const shift = entity
                .getIn(field.absoluteAccessor)
                ?.map(shift => shift.shiftId)
                .toArray();
            const dataTestId = testIds.rider.shiftId;

            return {
                entity,
                shift,
                getShiftFilters: input => getEntityIdInputFilters({ input }),
                'data-test-id': dataTestId
            };
        }
    },
    {
        accessor: 'teams',
        label: 'riderForm.teams',
        type: FieldTypeEnum.Teams,
        helpText: 'riderForm.teamsTooltip',
        required: true,
        extraProps: () => ({
            testId: testIds.rider.teams
        }),
        testId: testIds.rider.teams
    },
    {
        accessor: 'pjpData.salesCategoryIds',
        type: FieldTypeEnum.SalesCategoryId,
        label: 'riderForm.salesCategories',
        placeholder: 'riderForm.salesCategoriesPlaceholder',
        required: ({ props, entity }) =>
            !arePjpFieldsDisabled(props.entitySettings, entity),
        helpText: 'riderForm.salesCategoriesTooltip',
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp'),
        extraProps: ({ entity, field }) => {
            const value = entity
                .getIn(field.absoluteAccessor)
                ?.map(salesCat => salesCat.salesCategoryId)
                .toArray();
            return {
                value
            };
        },
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value)
    },
    {
        accessor: 'pjpData.channels',
        type: FieldTypeEnum.Select,
        label: 'riderForm.channelPreference',
        placeholder: 'riderForm.channelPreferencePlaceholder',
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp'),
        options: ({ props }) =>
            props.entitySettings?.getPjpChannelField() || [],
        extraProps: ({ field }) => ({
            isMulti: true,
            value: field.value.length > 0 ? field.value : null
        }),
        getProcessedValueForField: ({ entity, field }) => {
            const salesChannels = entity.getIn(field.absoluteAccessor);
            return salesChannels.map(salesChannel => salesChannel.id).toArray();
        },
        onChangeHandler: ({ field, entity, value }) => {
            const updatedChannel = value.map(({ label: name, value: id }) => {
                return SalesChannel.fromJS({
                    id,
                    name
                });
            });
            return entity.setIn(
                field.absoluteAccessor,
                Immutable.List(updatedChannel)
            );
        }
    },
    {
        accessor: 'pjpData.revenue.amount.amount',
        label: ({ props, t }) => {
            const { revenueMonthCount } = props;
            return t('riderForm.revenueGeneratedMonth', {
                count: revenueMonthCount,
                revenueMonthCount,
                symbol: getSymbolFromCurrency(props?.defaultCurrency)
            });
        },
        type: FieldTypeEnum.Text,
        placeholder: 'riderForm.revenueGeneratedPlaceholder',
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp')
    },
    {
        accessor: 'pjpData.skusSoldCount',
        label: 'riderForm.skusSoldCount',
        helpText: 'riderForm.skusSoldCountTooltip',
        type: FieldTypeEnum.Text,
        placeholder: 'riderForm.skusSoldCountPlaceholder',
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp')
    },
    {
        accessor: 'competency.rating.rating',
        label: 'riderForm.rating',
        type: FieldTypeEnum.Text,
        placeholder: 'riderForm.ratingPlaceholder',
        halfWidthInput: true,
        extraProps: () => {
            return {
                rightIcon: '/ 5.00'
            };
        },
        testId: testIds.rider.rating
    },
    {
        accessor: 'competency.joiningDate',
        label: 'riderForm.joiningDate',
        helpText: 'riderForm.joiningDateTooltip',
        type: FieldTypeEnum.DateTimePicker,
        placeholder: 'riderForm.joiningDatePlaceholder',
        halfWidth: true,
        extraProps: ({ props }) => {
            const dateFormat = getDateFormat(props.localeSettings);
            return { dateFormat };
        },
        testId: testIds.rider.doj
    },
    {
        accessor: 'competency.experience',
        label: 'riderForm.experience',
        helpText: 'riderForm.experienceTooltip',
        placeholder: 'riderForm.experiencePlaceholder',
        type: FieldTypeEnum.Text,
        halfWidth: true,
        testId: testIds.rider.experience
    },
    {
        accessor: 'pjpData.zoneIds',
        label: 'riderForm.zones',
        helpText: 'riderForm.zoneTooltip',
        halfWidth: false,
        placeholder: 'riderForm.zoneIds',
        type: FieldTypeEnum.MultiChip,
        extraProps: ({ entity, t }) => {
            const { pjpData } = entity;
            const error = pjpData
                ? pjpData
                      .setAllValidationErrors()
                      .getValidationMessage('zoneIds')
                : null;
            return {
                zones: entity.pjpData?.zoneIds?.toJS(),
                error,
                label: t('riderForm.zones')
            };
        },
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(field.absoluteAccessor, Immutable.List(value));
        },
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp')
    },
    {
        accessor: 'shiftsInfo.workingDays',
        label: 'riderForm.workingDays',
        type: FieldTypeEnum.WorkingDays,
        extraProps: ({ t }) => {
            return {
                translationsObject: {
                    add: t('riderForm.addWorkingDays'),
                    change: t('riderForm.changeWorkingDays'),
                    startLocationPlaceholder: t(
                        'riderForm.startLocationPlaceholder'
                    ),
                    endLocationPlaceholder: t(
                        'riderForm.endLocationPlaceholder'
                    )
                }
            };
        },
        getProcessedValueForField: ({ entity }) => {
            return entity.shiftsInfo.initializeWeekDays();
        },
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp')
    },
    {
        accessor: 'pjpData.holidays',
        label: 'riderForm.holidays',
        helptext: 'riderForm.holidaysHelpText',
        component: HolidaySelector,
        extraProps: ({ entity, t }) => {
            const { holidays } = entity.get('pjpData');
            return { holidays: holidays || [], t };
        },
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(field.absoluteAccessor, Immutable.List(value));
        },
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp')
    },
    {
        accessor: 'type',
        type: FieldTypeEnum.Select,
        required: ({ props }) => !arePjpFieldsDisabled(props.entitySettings),
        label: 'riderForm.type',
        placeholder: 'riderForm.typePlaceholder',
        extraProps: ({ props }) => {
            return {
                disabled: arePjpFieldsDisabled(props.entitySettings)
            };
        },
        options: RiderTypeOptions,
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value);
        }
    },
    {
        accessor: 'customProperties',
        type: FieldTypeEnum.CustomFields,
        extraProps: () => {
            return {
                settingsName: 'riderSettings'
            };
        }
    }
];

const RIDER_INITIAL_FETCH_FIELDS = ['competency.skills'];

const SingleRiderEditContainer = entityMasterSingleEditFactory({
    MasterEntity: MasterRider,
    MasterActions: RiderActions,
    extraApiParams: {
        include: [
            IncludeTypeEnums.SalesCategories,
            IncludeTypeEnums.RiderPersona
        ]
    },
    EntityFetchAPI: riderApi,
    INITIAL_FETCH_FIELDS: RIDER_INITIAL_FETCH_FIELDS,
    FIELDS: RIDER_FIELDS,
    options: {
        ENTITY_NAME_ID,
        PARAM_ID: 'riderId',
        ENTITY_SETTINGS_NAME: 'riderSettings'
    },
    mapEntityStateToProps: ({ state, props, baseProps }) => {
        const countryISOCode = ClientSelectors.getCountryISOCodeFromLocaleSettings(
            state,
            props
        );
        const teams = personnelTeamsSelector(state, props);
        const dimensionsMapping = getClientDimensionsMapping(state, props);

        let unitConverter = null;
        if (dimensionsMapping) {
            unitConverter = new UnitConversionHelper({
                dimensionsMapping
            });
        }
        return {
            teams,
            revenueMonthCount:
                baseProps?.entitySettings?.riderSettings?.revenueMonthCount,
            defaultCurrency: baseProps?.localeSettings?.currency,
            defaultCountryISOCode: countryISOCode,
            unitConverter
        };
    }
});

export default SingleRiderEditContainer;
